import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import type { User, UserPreferences } from '@shared/types';
import { api } from '@/lib/api';

interface AuthMeResponse {
  user: User;
  preferences: UserPreferences | null;
}

interface LoginResponse {
  token: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  preferences: UserPreferences | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (token: string, name: string, password: string) => Promise<void>;
  logout: () => void;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'tool_crib_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  // Check auth status on mount
  useEffect(() => {
    if (token) {
      api
        .get<AuthMeResponse>('/auth/me')
        .then((res) => {
          if (res.success && res.data) {
            setUser(res.data.user);
            setPreferences(res.data.preferences);
          } else {
            // Invalid token
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
          }
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { email, password });
    if (res.success && res.data) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    } else {
      throw new Error(res.error || 'Login failed');
    }
  };

  const register = async (inviteToken: string, name: string, password: string) => {
    const res = await api.post<LoginResponse>('/auth/register', { token: inviteToken, name, password });
    if (res.success && res.data) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    } else {
      throw new Error(res.error || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setPreferences(null);
  };

  const updatePreferences = async (prefs: Partial<UserPreferences>) => {
    const res = await api.put('/api/users/me/preferences', prefs);
    if (res.success) {
      setPreferences((prev) => (prev ? { ...prev, ...prefs } : null));
    } else {
      throw new Error(res.error || 'Failed to update preferences');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        preferences,
        token,
        loading,
        login,
        register,
        logout,
        updatePreferences,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
